import mixpanel from 'mixpanel-browser';
import config from '../config';
import appConstants from '../utils/constants/app';
import Logger from './Logger';
import LocalStorage from './LocalStorage';

let isInitialized = false;

function init() {
  if (!isInitialized) {
    Logger.debug('Initializing mixpanel');
    mixpanel.init(config.mixpanel.token, {
      cross_subdomain_cookie: false,
      cookie_domain: window.location.hostname,
    });
    isInitialized = true;
  }
}

function getDistinctId() {
  if (!isInitialized) {
    return null;
  }
  return mixpanel.get_distinct_id();
}

function identify(id, isNewUser) {
  if (!isInitialized) {
    Logger.debug('Mixpanel identify skipped, not initialized', { userId: id });
    return;
  }
  const distinctIdBefore = mixpanel.get_distinct_id();
  let aliasResult = 'alias-not-called';
  if (isNewUser) {
    const result = mixpanel.alias(id);
    aliasResult = typeof result === 'object' ? 'alias-queued' : result;
  }
  mixpanel.identify(id);
  const distinctIdAfter = mixpanel.get_distinct_id();
  const aliasKey = mixpanel.get_property('__alias') || null;
  const details = {
    userId: id,
    isNewUser: !!isNewUser,
    distinctIdBefore,
    distinctIdAfter,
    aliasResult,
    aliasKey,
  };
  if (isNewUser && distinctIdAfter === id) {
    Logger.error(
      'Mixpanel identity merge failed, anonymous history orphaned',
      details
    );
  } else {
    Logger.debug('Mixpanel identify', details);
  }
}

function baseProperties(data) {
  return {
    Time: new Date().getHours(),
    Day: new Date().getDay(),
    'Sent from': appConstants.APP_NAME,
    Platform: appConstants.APP_NAME,
    'Source App': appConstants.SOURCE_APP,
    ...data,
  };
}

function clientContext() {
  return {
    distinctId: mixpanel.get_distinct_id(),
    visibilityState:
      typeof document === 'undefined' ? null : document.visibilityState,
    userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
  };
}

function track(event, data) {
  if (!isInitialized) {
    return;
  }
  mixpanel.track(event, baseProperties(data));
}

function trackWithConfirmation(event, data, { timeoutMs = 3000 } = {}) {
  if (!isInitialized) {
    Logger.error('Mixpanel track skipped, not initialized', { event });
    return;
  }
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) {
      return;
    }
    settled = true;
    Logger.error('Mixpanel track did not confirm in time', {
      event,
      timeoutMs,
      ...clientContext(),
    });
  }, timeoutMs);

  mixpanel.track(event, baseProperties(data), (status) => {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(timer);
    if (status === 0) {
      Logger.error('Mixpanel track explicitly failed', {
        event,
        status,
        ...clientContext(),
      });
      return;
    }
    Logger.debug('Mixpanel track confirmed', {
      event,
      status,
      confirms: mixpanel.get_config('batch_requests')
        ? 'enqueued'
        : 'delivered',
    });
  });
}

function signOut() {
  if (!isInitialized) {
    return;
  }
  Logger.debug('Mixpanel reset, clearing identity', {
    distinctId: mixpanel.get_distinct_id(),
  });
  track('User Logout');
  mixpanel.reset();
  LocalStorage.setItem('mixpanel_event_sent', false);
}

function setSuperProperties(data) {
  if (!isInitialized) {
    return;
  }
  mixpanel.register(data);
}

function setPeopleProperties(data) {
  if (!isInitialized) {
    return;
  }
  mixpanel.people.set(data);
}

const Mixpanel = {
  init,
  identify,
  getDistinctId,
  signOut,
  track,
  trackWithConfirmation,
  setSuperProperties,
  setPeopleProperties,
};

export default Mixpanel;
